// The cleanup judgement's seven questions, in the order the result panel
// asks them (plan Decision 6). Each is a probability, not a verdict: accept
// and review thresholds are host configuration, so the lab shows the
// numbers and says which direction is the worrying one, and draws no line.
import type { CleanupJudgement } from "@bindery/speech/contract";

export type JudgementQuestion = Exclude<
  keyof CleanupJudgement,
  "judge" | "version" | "sawPrompt"
>;

export interface QuestionSpec {
  readonly key: JudgementQuestion;
  // Phrased as the judge is asked it, about the cleaned text.
  readonly label: string;
  // Which end of [0, 1] is the one a reviewer wants: meaning preserved is
  // good when high; every change the cleanup was told not to make is good
  // when low.
  readonly wanted: "high" | "low";
}

export const MEANING: QuestionSpec = {
  key: "preservesMeaning",
  label: "preserves what the child meant",
  wanted: "high",
};

// The six things the cleanup prompt says not to do (openai-direct.ts's
// POST_PROCESSING_PROMPT, "Do not: …"), one question each.
export const CHANGES: readonly QuestionSpec[] = [
  { key: "contentAdded", label: "added content", wanted: "low" },
  { key: "contentRemoved", label: "removed content", wanted: "low" },
  { key: "wordChoiceChanged", label: "changed word choice", wanted: "low" },
  {
    key: "sentenceRestructured",
    label: "restructured sentences",
    wanted: "low",
  },
  { key: "grammarCorrected", label: "corrected grammar", wanted: "low" },
  {
    key: "pronunciationSpellingFixed",
    label: "fixed pronunciation-based spellings",
    wanted: "low",
  },
];

// How far a probability sits from the wanted end, in [0, 1]: 0 is exactly
// what a reviewer hopes for, 1 the opposite. Drives the bar's tint only.
export const concern = (spec: QuestionSpec, p: number): number =>
  spec.wanted === "high" ? 1 - p : p;
